// @flow

import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";

import sharedActionCreators from "@modules/shared/actionCreator";
import * as sharedSelectors from "@modules/shared/selectors";
import type { HierarchyTree } from "@modules/shared/reducer/hierarchy/types";
import type { ApprovableContainer } from "@modules/service/types";
import type { Rtw } from "@modules/service/reducer/rtw/types";
import * as accountSelectors from "@modules/account/selectors";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreators from "@modules/service/actionCreator";

import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "@components";
import { HierarchyScopeForm } from "@modules/service/components/hierarchy-scope";
import type { HierarchyAssignment } from "@modules/service/types";

type PropsFromState = $Exact<{
  isFirstCareUser: boolean,
  current: ?ApprovableContainer<Rtw>,
  hierarchy: ?HierarchyTree,
  draftAssignments: Array<HierarchyAssignment>,
  isRtwDraftAssignmentsValid: boolean,
}>;

type PropsFromDispatch = $Exact<{
  setAssignments: typeof actionCreators.SetRtwDraftAssignments,
  updateAssignments: typeof actionCreators.UpdateRtwAssignments,
}>;

type OwnProps = $Exact<{
  clientId: number,
  onClose: () => void,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class RtwEditAssignmentsModal extends Component<Props> {
  handleCancel = () => {
    this.props.onClose();
  };

  handleSubmitRtwDraft = () => {
    if (this.props.current && this.props.current.hierarchyAssignments) {
      this.props.updateAssignments({
        container: this.props.current,
        assignments: this.props.draftAssignments,
      });
      this.props.onClose();
    }
  };

  handleChanges = (data: Array<HierarchyAssignment>) => {
    this.props.setAssignments(data);
  };

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("RETURN_TO_WORK_EDIT_HIERARCHY_SCOPE_TITLE", {
            ...(this.props.current ? { processId: this.props.current.id } : {}),
          })}
        </ModalHeader>
        <ModalBody className="d-flex flex-column align-content-center">
          <div className="w-100 d-flex justify-content-center">
            <div className="col-sm-12">
              {this.props.hierarchy && this.props.draftAssignments ? (
                <HierarchyScopeForm
                  hierarchy={this.props.hierarchy}
                  assignments={this.props.draftAssignments}
                  onChange={this.handleChanges}
                />
              ) : null}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            onClick={this.handleSubmitRtwDraft}
            disabled={!this.props.isRtwDraftAssignmentsValid}
            materialIcon="save"
          >
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    isFirstCareUser: accountSelectors.isFirstCareUser(state),
    current: serviceSelectors.currentRtw(state),
    hierarchy: sharedSelectors.getHierarchyTree(state),
    draftAssignments: serviceSelectors.getRtwDraftAssignments(state),
    isRtwDraftAssignmentsValid: serviceSelectors.isRtwDraftAssignmentsValid(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setAssignments: actionCreators.SetRtwDraftAssignments,
  updateAssignments: actionCreators.UpdateRtwAssignments,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(RtwEditAssignmentsModal);
